import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "communityButton", "communityList", "communityText", "communityInput", "communityCheck",
    "provinceButton", "provinceList", "provinceText", "provinceInput",
    "municipalityButton", "municipalityList", "municipalityText", "municipalityInput"
  ]

  static values = {
    selectedCommunity: String,
    selectedProvince: String,
    selectedProvinceName: String,
    selectedMunicipality: String,
    selectedMunicipalityName: String,
    allProvinces: String,
    allMunicipalities: String,
    selectProvince: String,
    selectMunicipality: String

  }

  connect() {
    document.addEventListener('click', this.closeDropdowns.bind(this))

    // Initialize selections if params exist
    if (this.selectedCommunityValue) {
      this.communityInputTarget.value = this.selectedCommunityValue
      const communityElement = this.communityListTarget.querySelector(`[data-code="${this.selectedCommunityValue}"]`)

      if (communityElement) {
        const communityName = communityElement.querySelector('span').textContent
        this.communityTextTarget.textContent = communityName

        // Show checkmark for community
        this.communityListTarget.querySelectorAll('[data-location-filter-target="communityCheck"]')
          .forEach(check => check.classList.add('hidden'))
        communityElement.querySelector('[data-location-filter-target="communityCheck"]')
          .classList.remove('hidden')

        // Enable province select
        this.enableProvinceSelect()

        // If province is selected, show it immediately
        if (this.selectedProvinceValue) {
          // Create and append the province option
          const provinceItem = `
            <li class="relative cursor-pointer py-2 pr-9 pl-3 text-gray-900 hover:bg-[#0352ff] hover:text-white select-none"
                data-code="${this.selectedProvinceValue}"
                data-action="click->location-filter#selectProvince">
              <span class="block truncate font-normal">${this.selectedProvinceNameValue || this.selectedProvinceValue}</span>
              <span class="absolute inset-y-0 right-0 flex items-center pr-4 text-[#0352ff]"
                    data-location-filter-target="provinceCheck">
                <svg class="size-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clip-rule="evenodd" />
                </svg>
              </span>
            </li>
          `
          this.provinceListTarget.innerHTML = provinceItem

          // Set the selected province
          this.provinceInputTarget.value = this.selectedProvinceValue
          this.provinceTextTarget.textContent = this.selectedProvinceNameValue || this.selectedProvinceValue

          // Enable municipality select if province is selected
          this.enableMunicipalitySelect()

          // If municipality is selected, show it immediately
          if (this.selectedMunicipalityValue) {
            // Create and append the municipality option
            const municipalityItem = `
              <li class="relative cursor-pointer py-2 pr-9 pl-3 text-gray-900 hover:bg-[#0352ff] hover:text-white select-none"
                  data-code="${this.selectedMunicipalityValue}"
                  data-action="click->location-filter#selectMunicipality">
                <span class="block truncate font-normal">${this.selectedMunicipalityNameValue || this.selectedMunicipalityValue}</span>
                <span class="absolute inset-y-0 right-0 flex items-center pr-4 text-[#0352ff]"
                      data-location-filter-target="municipalityCheck">
                  <svg class="size-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clip-rule="evenodd" />
                  </svg>
                </span>
              </li>
            `
            this.municipalityListTarget.innerHTML = municipalityItem

            // Set the selected municipality
            this.municipalityInputTarget.value = this.selectedMunicipalityValue
            this.municipalityTextTarget.textContent = this.selectedMunicipalityNameValue || this.selectedMunicipalityValue
          }
        }

        // Then fetch the complete lists in the background
        this.updateProvinces()
        if (this.selectedProvinceValue) {
          this.updateMunicipalities()
        }
      }
    }
  }



  disconnect() {
    document.removeEventListener('click', this.closeDropdowns.bind(this))
  }

  toggleMunicipalityList(event) {
    if (this.municipalityButtonTarget.disabled) return
    event.stopPropagation()
    this.municipalityListTarget.classList.toggle('hidden')
    this.communityListTarget.classList.add('hidden')
    this.provinceListTarget.classList.add('hidden')
  }

  selectMunicipality(event) {
    const item = event.currentTarget
    const code = item.dataset.code
    const name = item.querySelector('span').textContent

    this.municipalityTextTarget.textContent = name
    this.municipalityInputTarget.value = code
    this.municipalityListTarget.classList.add('hidden')

    this.municipalityListTarget.querySelectorAll('[data-location-filter-target="municipalityCheck"]')
      .forEach(check => check.classList.add('hidden'))
    item.querySelector('[data-location-filter-target="municipalityCheck"]').classList.remove('hidden')
  }


  enableMunicipalitySelect() {
    this.municipalityButtonTarget.disabled = false
    this.municipalityButtonTarget.classList.remove('cursor-not-allowed', 'bg-gray-50', 'text-gray-500', 'outline-gray-200')
    this.municipalityButtonTarget.classList.add('cursor-pointer', 'bg-white', 'text-gray-900', 'outline-gray-300')
  }

  disableMunicipalitySelect() {
    this.municipalityButtonTarget.disabled = true
    this.municipalityButtonTarget.classList.remove('cursor-pointer', 'bg-white', 'text-gray-900', 'outline-gray-300')
    this.municipalityButtonTarget.classList.add('cursor-not-allowed', 'bg-gray-50', 'text-gray-500', 'outline-gray-200')
    this.municipalityListTarget.innerHTML = ''
    this.municipalityTextTarget.textContent = this.selectMunicipalityValue
    this.municipalityInputTarget.value = ''
  }

  closeDropdowns(event) {
    if (!this.element.contains(event.target)) {
      this.communityListTarget.classList.add('hidden')
      this.provinceListTarget.classList.add('hidden')
      this.municipalityListTarget.classList.add('hidden')
    }
  }

  toggleCommunityList(event) {
    event.stopPropagation()
    this.communityListTarget.classList.toggle('hidden')
    this.provinceListTarget.classList.add('hidden')
  }

  toggleProvinceList(event) {
    if (this.provinceButtonTarget.disabled) return
    event.stopPropagation()
    this.provinceListTarget.classList.toggle('hidden')
    this.communityListTarget.classList.add('hidden')
  }

  async selectCommunity(event) {
    const item = event.currentTarget
    const code = item.dataset.code
    const name = item.querySelector('span').textContent

    // Update the community selection
    this.communityTextTarget.textContent = name
    this.communityInputTarget.value = code
    this.communityListTarget.classList.add('hidden')

    // Update checkmark
    this.communityListTarget.querySelectorAll('[data-location-filter-target="communityCheck"]')
      .forEach(check => check.classList.add('hidden'))
    item.querySelector('[data-location-filter-target="communityCheck"]').classList.remove('hidden')

    // Clear province selection
    this.disableProvinceSelect()

    // Clear municipality selection
    this.disableMunicipalitySelect()

    // Fetch and update provinces
    await this.updateProvinces()
  }

  selectProvince(event) {
    const item = event.currentTarget
    const code = item.dataset.code
    const name = item.querySelector('span').textContent

    this.provinceTextTarget.textContent = name
    this.provinceInputTarget.value = code
    this.provinceListTarget.classList.add('hidden')

    this.provinceListTarget.querySelectorAll('[data-location-filter-target="provinceCheck"]')
      .forEach(check => check.classList.add('hidden'))
    item.querySelector('[data-location-filter-target="provinceCheck"]').classList.remove('hidden')

    // Clear municipality selection
    this.disableMunicipalitySelect()

    this.updateMunicipalities()
  }

  enableProvinceSelect() {
    this.provinceButtonTarget.disabled = false
    this.provinceButtonTarget.classList.remove('cursor-not-allowed', 'bg-gray-50', 'text-gray-500', 'outline-gray-200')
    this.provinceButtonTarget.classList.add('cursor-pointer', 'bg-white', 'text-gray-900', 'outline-gray-300')
  }

  disableProvinceSelect() {
    this.provinceButtonTarget.disabled = true
    this.provinceButtonTarget.classList.remove('cursor-pointer', 'bg-white', 'text-gray-900', 'outline-gray-300')
    this.provinceButtonTarget.classList.add('cursor-not-allowed', 'bg-gray-50', 'text-gray-500', 'outline-gray-200')
    this.provinceListTarget.innerHTML = ''
    this.provinceTextTarget.textContent = this.selectProvinceValue
    this.provinceInputTarget.value = ''
  }

  async updateProvinces() {
    const communityCode = this.communityInputTarget.value

    if (!communityCode) {
      this.disableProvinceSelect()
      return
    }

    try {
      const response = await fetch(`/autonomous_community/${communityCode}/list_provinces`)
      const provinces = await response.json()

      // Sort provinces alphabetically by name
      provinces.sort((a, b) => a.name.localeCompare(b.name))

      // Enable the province select if not already enabled
      this.enableProvinceSelect()

      // Update province list while preserving the selection
      const currentSelection = this.provinceInputTarget.value

      this.provinceListTarget.innerHTML = `
            <!-- "All" option -->
            <li class="relative cursor-pointer py-2 pr-9 pl-3 text-gray-900 hover:bg-[#0352ff] hover:text-white select-none"
                data-code=""
                data-action="click->location-filter#selectProvince">

                <span class="block truncate font-normal">${this.allProvincesValue}</span>
                <span class="absolute inset-y-0 right-0 flex items-center pr-4 text-[#0352ff] ${currentSelection === '' ? '' : 'hidden'}"
                      data-location-filter-target="provinceCheck">
                    <svg class="size-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clip-rule="evenodd" />
                    </svg>
                </span>
            </li>
        ` + provinces.map(province => `
            <li class="relative cursor-pointer py-2 pr-9 pl-3 text-gray-900 hover:bg-[#0352ff] hover:text-white select-none"
                data-code="${province.code}"
                data-action="click->location-filter#selectProvince">
                <span class="block truncate font-normal">${province.name}</span>
                <span class="absolute inset-y-0 right-0 flex items-center pr-4 text-[#0352ff] ${province.code === currentSelection ? '' : 'hidden'}"
                      data-location-filter-target="provinceCheck">
                    <svg class="size-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clip-rule="evenodd" />
                    </svg>
                </span>
            </li>
        `).join('')

    } catch (error) {
      console.error("Error fetching provinces:", error)
      this.disableProvinceSelect()
    }
  }

  async updateMunicipalities() {
    const provinceCode = this.provinceInputTarget.value

    if (!provinceCode) {
      this.disableMunicipalitySelect()
      return
    }

    try {
      const response = await fetch(`/provinces/${provinceCode}/list_municipalities`)
      const municipalities = await response.json()

      // Sort municipalities alphabetically by name
      municipalities.sort((a, b) => a.name.localeCompare(b.name))

      this.enableMunicipalitySelect()

      const currentSelection = this.municipalityInputTarget.value

      this.municipalityListTarget.innerHTML = `
            <!-- "All" option -->
            <li class="relative cursor-pointer py-2 pr-9 pl-3 text-gray-900 hover:bg-[#0352ff] hover:text-white select-none"
                data-code=""
                data-action="click->location-filter#selectMunicipality">
                <span class="block truncate font-normal">${this.allMunicipalitiesValue}</span>
                <span class="absolute inset-y-0 right-0 flex items-center pr-4 text-[#0352ff] ${currentSelection === '' ? '' : 'hidden'}"
                      data-location-filter-target="municipalityCheck">
                    <svg class="size-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clip-rule="evenodd" />
                    </svg>
                </span>
            </li>
        ` + municipalities.map(municipality => `
            <li class="relative cursor-pointer py-2 pr-9 pl-3 text-gray-900 hover:bg-[#0352ff] hover:text-white select-none"
                data-code="${municipality.code}"
                data-action="click->location-filter#selectMunicipality">
                <span class="block truncate font-normal">${municipality.name}</span>
                <span class="absolute inset-y-0 right-0 flex items-center pr-4 text-[#0352ff] ${municipality.code === currentSelection ? '' : 'hidden'}"
                      data-location-filter-target="municipalityCheck">
                    <svg class="size-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clip-rule="evenodd" />
                    </svg>
                </span>
            </li>
        `).join('')

    } catch (error) {
      console.error("Error fetching municipalities:", error)
      this.disableMunicipalitySelect()
    }
  }
}
